// The scrollable conversation thread: renders inline action cards and text bubbles (or the empty
// state). Tool confirmations render in the workspace panel only (see ChatPage's surfacePanel), not
// here. Handlers are passed in from ChatPage.
//
// Scroll behaviour lives here (it owns the scroll container):
//  • On load / conversation switch it JUMPS to the bottom (no smooth animation) so a refresh shows
//    the latest messages directly, with no visible scroll-down effect.
//  • It sticks to the bottom when a new message arrives *only if* the user was already near the
//    bottom — reading older history is never interrupted.
//  • Scrolling near the top triggers `onLoadOlder` (pagination); when the older page prepends, the
//    viewport is anchored so the content the user was reading stays put (no jump).
import { useLayoutEffect, useRef } from 'react';
import { EmptyState } from '@/components/chat/EmptyState';
import { TypingDots } from '@/components/chat/TypingDots';
import { MessageBubble, type TextMessage } from '@/components/chat/MessageBubble';
import { ActionCard, type ActionCardMessage } from '@/components/chat/ActionCard';
import { TurnSteps } from '@/components/chat/AgentStepsView';
import type { SamModule } from '@/components/SAM/samActionTypes';
import type { Rec } from '@/components/chat/ChatPills';
import type { ChatMessage } from '../types';

/** Bubble ids are the live `message_id` (no suffix) or a reloaded `run_id:a`; the run lookup
 *  matches either, so strip any `:suffix` to get the turn key. */
const turnKey = (id: string) => id.split(':')[0];

/** Distance (px) from the bottom within which we consider the user "at the bottom" and keep them
 *  pinned as new messages arrive. */
const STICK_THRESHOLD = 120;
/** Distance (px) from the top that triggers loading the next older page. */
const LOAD_TOP_THRESHOLD = 80;

const TypingIndicator = () => (
  <div className="flex items-end gap-2 justify-start">
    <div className="flex items-center gap-1 bg-ui-card border border-ui-border rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
      <TypingDots dotClassName="w-2 h-2 bg-brand-dark" />
    </div>
  </div>
);

export const ChatThread = ({
  threadRef,
  messages,
  onActionView,
  onRetryMessage,
  conversationId,
  debugMode,
  feedbackEnabled = false,
  isWaiting = false,
  hasMore = false,
  loadingOlder = false,
  onLoadOlder,
}: {
  threadRef: React.RefObject<HTMLDivElement | null>;
  /** Already filtered to text / action messages — confirm cards live in the workspace panel. */
  messages: ChatMessage[];
  onActionView: (module: SamModule, filter: Rec, title?: string, caption?: string, recordId?: string | null) => void;
  /** Re-sends a user turn whose delivery failed (gateway ack timeout/error). */
  onRetryMessage?: (msg: TextMessage) => void;
  conversationId: string;
  /** When true, each completed assistant reply gets a "View agent steps" inspector. */
  debugMode: boolean;
  /** When true, each completed assistant reply shows a "Was this helpful?" feedback prompt. */
  feedbackEnabled?: boolean;
  /** When true, shows a typing indicator while waiting for the first response chunk. */
  isWaiting?: boolean;
  /** Whether older messages remain to be loaded (drives scroll-up pagination). */
  hasMore?: boolean;
  /** True while an older page is loading (shows a spinner; guards duplicate fetches). */
  loadingOlder?: boolean;
  /** Fetch + prepend the next older page. */
  onLoadOlder?: () => void;
}) => {
  // Track the conversation so a switch triggers a jump to the bottom (fresh thread).
  const lastConvRef = useRef<string>('');
  // Whether the user was near the bottom before the latest render (drives stick-to-bottom).
  const nearBottomRef = useRef(true);
  // Set just before an older page prepends; carries the pre-prepend scroll metrics so we can restore
  // the viewport position afterwards (otherwise the added content above would shove everything down).
  const prependRef = useRef<{ prevHeight: number; prevTop: number } | null>(null);

  const handleScroll = () => {
    const el = threadRef.current;
    if (!el) return;
    nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < STICK_THRESHOLD;
    if (el.scrollTop <= LOAD_TOP_THRESHOLD && hasMore && !loadingOlder && onLoadOlder) {
      prependRef.current = { prevHeight: el.scrollHeight, prevTop: el.scrollTop };
      onLoadOlder();
    }
  };

  useLayoutEffect(() => {
    const el = threadRef.current;
    if (!el) return;

    // Conversation switched (or first mount): jump straight to the newest messages, no animation.
    if (lastConvRef.current !== conversationId) {
      lastConvRef.current = conversationId;
      prependRef.current = null;
      el.scrollTop = el.scrollHeight;
      nearBottomRef.current = true;
      return;
    }

    // An older page just prepended: keep the same content under the user's eyes by offsetting the
    // scroll position by exactly the height that was added above.
    if (prependRef.current) {
      const { prevHeight, prevTop } = prependRef.current;
      prependRef.current = null;
      el.scrollTop = prevTop + (el.scrollHeight - prevHeight);
      return;
    }

    // New message appended: stick to the bottom only if the user was already there.
    if (nearBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [messages, conversationId, threadRef]);

  return (
    <div ref={threadRef} onScroll={handleScroll} className="h-full overflow-y-auto no-scrollbar px-4 py-2">
      {messages.length > 0 || isWaiting ? (
        <div className="space-y-3 pb-2">
          {loadingOlder && (
            <div className="flex justify-center py-2">
              <span className="w-4 h-4 rounded-full border-2 border-ui-border border-t-brand-bright animate-spin" />
            </div>
          )}
          {messages.map(m => {
            if (m.kind === 'action') {
              return <ActionCard key={m.id} msg={m as ActionCardMessage} onView={(mod, f) => onActionView(mod, f, (m as ActionCardMessage).title, (m as ActionCardMessage).caption, (m as ActionCardMessage).recordId)} />;
            }
            const tm = m as TextMessage;
            const showSteps = debugMode && tm.role === 'assistant' && !tm.streaming && !!conversationId;
            // A live voice turn's id is a throwaway local id (`a-rt-…`). Each start→stop is its OWN
            // run, so target this bubble's session run_id to show ONLY that session's steps (falling
            // back to the conversation id for older payloads without runId). Reloaded voice turns
            // arrive as `run_id:a:i` and resolve via the normal run_id path.
            const turnId = tm.voice ? (tm.runId || conversationId) : turnKey(tm.id);
            return (
              <div key={m.id} data-mid={m.id}>
                <MessageBubble
                  msg={tm}
                  onRetry={tm.deliveryStatus === 'failed' ? () => onRetryMessage?.(tm) : undefined}
                  feedbackEnabled={feedbackEnabled}
                  conversationId={conversationId}
                />
                {showSteps && <TurnSteps conversationId={conversationId} turnId={turnId} />}
              </div>
            );
          })}
          {isWaiting && <TypingIndicator />}
        </div>
      ) : (
        <EmptyState />
      )}
    </div>
  );
};
